import { useSeoMeta } from '@unhead/react';
import { Link } from 'react-router-dom';
import { PenLine, Upload, AlertCircle } from 'lucide-react';

import { Layout } from '@/components/Layout';
import { ArticleCard } from '@/components/ArticleCard';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Skeleton } from '@/components/ui/skeleton';
import { useCurrentUser } from '@/hooks/useCurrentUser';
import { useArticles } from '@/hooks/useArticles';

export default function MyArticles() {
  useSeoMeta({
    title: 'My Articles | Substr',
    description: 'View and manage your published Nostr blog posts.',
  });

  const { user } = useCurrentUser();

  if (!user) {
    return (
      <Layout>
        <div className="max-w-2xl mx-auto text-center py-20">
          <div className="size-16 rounded-2xl bg-primary/10 flex items-center justify-center mx-auto mb-6">
            <PenLine className="size-8 text-primary" />
          </div>
          <h1 className="text-3xl font-bold font-serif mb-3">My Articles</h1>
          <p className="text-lg text-muted-foreground mb-6">
            Sign in to see your published articles.
          </p>
          <div className="flex items-center justify-center gap-2 text-sm text-muted-foreground">
            <AlertCircle className="size-4" />
            Use the Join button in the top right to get started.
          </div>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <MyArticlesFeed pubkey={user.pubkey} />
    </Layout>
  );
}

function MyArticlesFeed({ pubkey }: { pubkey: string }) {
  const { data: articles, isLoading } = useArticles(pubkey);

  return (
    <div className="max-w-3xl mx-auto">
      <div className="flex items-center justify-between mb-8">
        <div>
          <h1 className="text-3xl md:text-4xl font-bold font-serif mb-1">My Articles</h1>
          <p className="text-muted-foreground">
            {articles?.length ?? 0} article{articles?.length === 1 ? '' : 's'} published
          </p>
        </div>
        <Link to="/import">
          <Button>
            <Upload className="size-4 mr-2" />
            Import
          </Button>
        </Link>
      </div>

      {isLoading ? (
        <div className="space-y-4">
          {Array.from({ length: 3 }).map((_, i) => (
            <Card key={i}>
              <CardContent className="p-5">
                <div className="flex gap-5">
                  <div className="flex-1 space-y-3">
                    <div className="flex items-center gap-2">
                      <Skeleton className="size-6 rounded-full" />
                      <Skeleton className="h-4 w-20" />
                    </div>
                    <Skeleton className="h-6 w-3/4" />
                    <Skeleton className="h-4 w-full" />
                    <Skeleton className="h-3 w-32" />
                  </div>
                  <Skeleton className="hidden sm:block w-32 h-24 rounded-lg" />
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      ) : articles && articles.length > 0 ? (
        <div className="divide-y">
          {articles.map((event) => (
            <ArticleCard key={event.id} event={event} />
          ))}
        </div>
      ) : (
        <Card className="border-dashed">
          <CardContent className="py-16 px-8 text-center">
            <div className="size-14 rounded-2xl bg-muted flex items-center justify-center mx-auto mb-4">
              <PenLine className="size-7 text-muted-foreground" />
            </div>
            <h3 className="text-lg font-semibold mb-2">No articles yet</h3>
            <p className="text-muted-foreground max-w-sm mx-auto mb-6">
              Import your first Substack article to get started.
            </p>
            <Link to="/import">
              <Button>
                <Upload className="size-4 mr-2" />
                Import Article
              </Button>
            </Link>
          </CardContent>
        </Card>
      )}
    </div>
  );
}
